import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { takeUntil, catchError, throwError, Subject, BehaviorSubject, Observable, tap } from 'rxjs';
import { ConfigService } from './core/config.service';
import { UserKeycloak, UserWithRolesDTO } from '../interfaces/keycloak/UserKeycloak';

@Injectable({
  providedIn: 'root',
})
export class GestionComptesService implements OnDestroy {
  private readonly id = new BehaviorSubject<number>(0);
  private readonly _destroying$ = new Subject<void>();
  public idAdherent = this.id.asObservable();

  constructor(private http: HttpClient, private configService: ConfigService) {}

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Updates the current ID with the provided value.
   *
   * @param value - The new value to set for the ID.
   */
  updateId(value: any) {
    this.id.next(value);
  }

  getId(): number {
    return this.id.value;
  }

  /**
   * Fetches users associated with a specific `idAdherent`.
   *
   * This function makes a `GET` request to the backend to retrieve a list of users related to the provided `idAdherent`.
   * The response is an array of users with their roles. If an error occurs during the HTTP request, it is caught and returned as an observable.
   *
   * @param {number} idAdherent - The ID of the adherent whose users are to be fetched.
   *
   * @returns {Observable<UserWithRolesDTO[]>} An observable containing the list of users with their roles associated with the given `idAdherent`.
   */
  getUsersByIdAdherent(idAdherent: number) {
    return this.http
      .get<UserWithRolesDTO[]>(
        `${this.configService.config.backendUri}/gestion-comptes/users-by-idAdherent/${idAdherent}`,
      )
      .pipe(
        takeUntil(this._destroying$),
        tap((users: UserWithRolesDTO[]) => {
          users = users.filter(u => u.isInactivated == false);
          return users;
        }),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Sends an HTTP request to update the roles of users.
   *
   * This function makes a `PUT` request to the backend to update the roles of the users provided in the `userWithRolesDTO` array.
   * It handles any errors that might occur during the HTTP request by catching the error and returning it as an observable.
   *
   * @param {UserWithRolesDTO[]} userWithRolesDTO - An array of users with updated roles to be sent to the backend.
   *
   * @returns {Observable<any>} An observable that represents the result of the HTTP request. It will either emit the response or throw an error.
   */
  updateUserRole(userWithRolesDTO: UserWithRolesDTO[]): Observable<any> {
    return this.http
      .put(`${this.configService.config.backendUri}/gestion-comptes/users/roles`, userWithRolesDTO)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Create a new User with roles in backend API.
   * @returns An Observable list of UserWithRolesDTO.
   * @param usersList
   */
  public createUsers(
    idAdherent: number,
    users: UserWithRolesDTO[],
  ): Observable<UserWithRolesDTO[]> {
    return this.http
      .post<UserWithRolesDTO[]>(
        `${this.configService.config.backendUri}/gestion-comptes/users/${idAdherent}`,
        users,
        {
          reportProgress: true,
          responseType: 'json',
        },
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Send an email containing the new Users list for them to be validated by Hygie31
   * @returns An Observable list of UserWithRolesDTO.
   * @param usersList liste des utilisateurs a valider
   * @param idAdherent identifiant technique de l'officine
   * @param emailTitulaire adresse email du titulaire qui a fait la demande
   */
  public sendMailCreateUsers(
    users: UserWithRolesDTO[],
    idAdherent: number,
    emailTitulaire: string,
  ): Observable<UserWithRolesDTO[]> {
    let url: string = `${this.configService.config.backendUri}/gestion-comptes/users/send-mail/${idAdherent}/${emailTitulaire}`;
    return this.http
      .post<UserWithRolesDTO[]>(url, users, {
        reportProgress: true,
        responseType: 'json',
      })
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Sends an HTTP request to inactivate a user in Keycloak.   *
   * This function makes a `PUT` request to the backend to inactivate a user in Keycloak.
   * It handles any errors that might occur during the HTTP request by catching the error and returning it as an observable.
   * @param {string} userId - The Keycloak guid identifier of the user who will be disabled.
   * @returns {Observable<any>} An observable that represents the result of the HTTP request. It will either emit the response or throw an error.
   */
  inactivateUser(userId: string): Observable<any> {
    return this.http
      .put(
        `${this.configService.config.backendUri}/gestion-comptes/users/inactivate/${userId}`,
        null,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }
}
